import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { TextBlockView } from '@plone/volto-slate/blocks/Text';
import type { BlockViewProps } from '@plone/types';

import styles from '@redturtle/qubicaamf-theme/components/Blocks/CardWithImages/styles.module.css';
import type { CardWithImagesData } from '@redturtle/qubicaamf-theme/components/Blocks/CardWithImages/schema';

import CTA from '@redturtle/qubicaamf-theme/components/Blocks/commons/CTA';
import config from '@plone/registry';
import { Button } from 'react-aria-components';
import PlusSVG from '../../Icons/PlusSVG';
import MinusSVG from '../../Icons/MinusSVG';

type Props = BlockViewProps & {
  data: CardWithImagesData;
};

export default function View({ data, className, style }: Props) {
  const img_column_width = data.img_column_width
    ? parseInt(data.img_column_width)
    : 6;

  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [cardIsExpanded, setCardIsExpanded] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !targetRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const targetRect = targetRef.current.getBoundingClientRect();

      // Check if the bottom of targetRef is lower than the bottom of containerRef
      setIsOverflowing(targetRect.bottom > containerRect.bottom);
    };

    // Perform the check once when the component mounts
    checkOverflow();
  }, []); // Empty dependency array to run once after component mounts

  const Container = config.getComponent('Container').component || 'div';
  const Image = config.getComponent('Image').component;

  return (
    <section
      className={cx('block-cardwithimages', styles.block, className)}
      style={style}
      aria-label={data.title}
    >
      <Container
        className={cx('block-cardwithimages-container', styles.container)}
      >
        <div className={cx('block-cardwithimages-card', styles.card)}>
          <div
            className={cx(
              'block-cardwithimages-image-wrapper',
              `column-width-${img_column_width}`,
              styles['image-wrapper'],
              styles[`column-width-${img_column_width}`],
            )}
          >
            {data.image && (
              <Image
                key={data.image['@id']}
                className={cx('block-cardwithimages-image', styles.image)}
                sizes="(min-width: 700px) 390px"
                item={data.image}
                loading="lazy"
                alt=""
              />
            )}
          </div>
          <div
            className={cx(
              'block-cardwithimages-body',
              `column-width-${12 - img_column_width}`,
              styles.body,
              styles[`column-width-${12 - img_column_width}`],
              { 'expanded-card': cardIsExpanded },
            )}
            ref={containerRef}
          >
            {data.title && (
              <h2 className={cx('block-cardwithimages-title', styles.title)}>
                {data.title}
              </h2>
            )}
            {data.text && (
              <div
                className={cx('block-cardwithimages-text', styles.text)}
                ref={targetRef}
                aria-live="polite"
                role="region"
              >
                <TextBlockView data={{ value: data.text ?? {} }} />
              </div>
            )}
            {data.linkHref?.[0] && (
              <div className={styles.ctaContainer}>
                <CTA
                  href={data.linkHref[0]['@id']}
                  openLinkInNewTab={false}
                  ctaButtonStyle={data.ctaButtonStyle}
                  linkIcon={data.linkIcon}
                  cta_color={data.cta_color}
                >
                  {data.linkTitle}
                </CTA>
              </div>
            )}
            {/* hidden everywhere but the expandable grid */}
            <div
              className={cx(
                'expand-button-container',
                styles.hiddenExpandButton,
                {
                  'visible-expand-button': isOverflowing,
                },
              )}
              aria-hidden="true"
            >
              <Button
                className="expand-button"
                onPress={() => setCardIsExpanded(!cardIsExpanded)}
              >
                {!cardIsExpanded ? <PlusSVG /> : <MinusSVG />}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
